import { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DollarSign, ShoppingBag, CalendarClock, ShoppingCart } from "lucide-react";

export default function Overview() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, []);

  if (data === null) return <p className="text-zinc-500">Loading…</p>;
  if (data === false) return <p className="text-red-400">Failed to load analytics.</p>;

  const stats = [
    { label: "Revenue", value: fmt(data.revenue), icon: DollarSign, testId: "stat-revenue" },
    { label: "Marketing Orders", value: data.total_orders, icon: ShoppingBag, testId: "stat-orders" },
    { label: "Bookings", value: data.total_bookings, icon: CalendarClock, testId: "stat-bookings" },
    { label: "Abandoned Carts", value: data.abandoned_carts, icon: ShoppingCart, testId: "stat-abandoned" },
  ];

  return (
    <div data-testid="admin-overview">
      <h1 className="font-display text-4xl text-white mb-8">Overview</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testId} className="border border-white/10 bg-[#0A0A0A] p-5">
            <s.icon className="w-4 h-4 text-brand mb-3" />
            <p className="font-display text-3xl text-white">{s.value}</p>
            <p className="text-xs text-zinc-500 mt-1 uppercase tracking-widest">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div className="border border-white/10 bg-[#0A0A0A] p-6">
          <h2 className="text-sm font-semibold text-white mb-6 uppercase tracking-widest">Orders by Service</h2>
          {data.orders_by_service.length === 0 ? (
            <p className="text-xs text-zinc-500">No paid orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.orders_by_service.slice(0, 8)}>
                <CartesianGrid stroke="#27272A" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#71717A", fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fill: "#71717A", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A", fontSize: 12 }} />
                <Bar dataKey="count" fill="#2B37FF" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="border border-white/10 bg-[#0A0A0A] p-6">
          <h2 className="text-sm font-semibold text-white mb-6 uppercase tracking-widest">Booking Pipeline</h2>
          {Object.keys(data.pipeline).length === 0 ? (
            <p className="text-xs text-zinc-500">No bookings yet.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {Object.entries(data.pipeline).map(([status, count]) => (
                <div key={status} data-testid={`pipeline-${status}`} className="border border-white/10 px-4 py-3">
                  <p className="font-display text-2xl text-brand">{count}</p>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">{status.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>
          )}
          <h2 className="text-sm font-semibold text-white mt-10 mb-4 uppercase tracking-widest">Bookings by Type</h2>
          <div className="flex flex-wrap gap-3">
            {data.bookings_by_type.map((b) => (
              <div key={b.name} className="border border-white/10 px-4 py-3">
                <p className="font-display text-2xl text-white">{b.count}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">{b.name}s</p>
              </div>
            ))}
            {data.bookings_by_type.length === 0 && <p className="text-xs text-zinc-500">None yet.</p>}
          </div>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-10">
        <h2 className="text-sm font-semibold text-white mb-4 uppercase tracking-widest">Recent Client Activity</h2>
        {activity.length === 0 ? (
          <p className="text-xs text-zinc-500">No tracked activity yet — page views, cart and checkout events will appear here.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto" data-testid="activity-feed">
            {activity.map((e) => (
              <p key={e.id} className="text-xs text-zinc-400 py-1.5 border-b border-white/5">
                <span className="text-zinc-600">{new Date(e.created_at).toLocaleString()}</span>
                {" — "}<span className="text-zinc-200">{e.event.replace(/_/g, " ")}</span>
                {e.page && <> · {e.page}</>}
                {e.email && <> · <span className="text-brand">{e.email}</span></>}
                {e.detail && <> · {e.detail}</>}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-6">
        <h2 className="text-sm font-semibold text-white mb-4 uppercase tracking-widest">Abandoned Carts</h2>
        {data.abandoned.length === 0 ? (
          <p className="text-xs text-zinc-500">No unpaid carts right now.</p>
        ) : (
          <table className="w-full text-sm" data-testid="abandoned-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="py-2">Client</th><th>Email</th><th>Total</th><th>Reminder</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {data.abandoned.map((o) => (
                <tr key={o.id} className="border-b border-white/5 text-zinc-300">
                  <td className="py-2.5">{o.artist}</td>
                  <td>{o.email}</td>
                  <td>{fmt(o.total)}</td>
                  <td>{o.reminder_sent ? <span className="text-brand">Sent</span> : "Pending"}</td>
                  <td className="text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
